/**
 * A non-2xx response, surfaced only to callers that opt in via
 * `ExecuteOptions.throwOnHttpError`.
 *
 * `execute` has never checked `response.ok`: it reads the body and parses it,
 * so a 500 with an empty body is indistinguishable from an empty result and a
 * 500 with an HTML body throws a `SyntaxError`. Every existing caller is built
 * on that, and several deliberately treat "no data" as a valid outcome, so the
 * check is opt-in rather than global — making it unconditional would silently
 * change how every list in the app reacts to a failing API.
 *
 * Lives apart from `useFetchWithMsal` so that code which only needs to
 * *classify* a failure does not pull React and MSAL in with it.
 */
/**
 * The part of a ProblemDetails body the client acts on.
 *
 * `source` is the name of the service that failed, which the API adds when the failure was in
 * something it depends on rather than in itself. It is one of a closed list the API owns - never
 * text relayed from the upstream - so it is safe to put on a screen.
 */
export interface ProblemDetailsLike {
  source?: string;
  title?: string;
  status?: number;
}

export class HttpStatusError extends Error {
  constructor(
    readonly status: number,
    readonly url: string,
    /**
     * The parsed response body, when there was one and it was JSON.
     *
     * Carried on the error because by the time a caller catches this the response is gone, and the
     * body is where the API says WHICH service failed. Undefined for an empty or non-JSON body -
     * an HTML error page from a gateway, say - which is itself a useful distinction: a failure with
     * no problem body did not come from our API.
     */
    readonly problem?: ProblemDetailsLike,
  ) {
    super(`HTTP ${status} for ${url}`);
    this.name = "HttpStatusError";
  }
}
